import { UserRoles } from '../constants'
import { getRoleName } from '../constants/UserRoles'

// Define role restrictions
export const ROLE_RESTRICTIONS = {
    [UserRoles.IMPERSONATOR]: {
        createOnly: false, // false means don't show in create form
        senseOnly: true, // true means only sense users can set this role
        targetSenseOnly: true, // true means only sense users can have this role
    },
    [UserRoles.BETA_TESTER]: {
        createOnly: true,
        senseOnly: true,
        targetSenseOnly: false,
    },
    [UserRoles.SYSTEM_ADMIN]: {
        createOnly: false,
        senseOnly: true,
        targetSenseOnly: true,
        neverSelectable: true, // Never allow setting from frontend - DB only
    },
    [UserRoles.REGULAR]: {
        alwaysRequired: true, // Always required - cannot be removed
    },
    [UserRoles.SHOP_USER]: {
        senseOnly: true,
        targetSenseOnly: false,
    },
    [UserRoles.CL_LIBRARIAN]: {
        createOnly: false,
        senseOnly: false, // Any admin can assign this role
        targetSenseOnly: false, // Any user can have this role
    },
}

/**
 * Get role options for forms/selects with proper filtering based on user permissions
 * @param {boolean} isSenseUserOrImpersonated - Whether current user is from Sense or impersonated
 * @param {boolean} isTargetUserFromSense - Whether target user is from Sense (for edit forms)
 * @param {boolean} isCreateForm - Whether this is a create form or edit form
 * @returns {Array} Array of role options with value, label, and disabled properties
 */
export const getRoleOptions = (isSenseUserOrImpersonated, isTargetUserFromSense = false, isCreateForm = false) => {
    return Object.values(UserRoles)
        .filter(role => {
            const restrictions = ROLE_RESTRICTIONS[role]
            if (!restrictions) return true // No restrictions for this role

            // Never allow SYSTEM_ADMIN to be selectable in any form
            if (restrictions.neverSelectable) {
                return false
            }

            // Hide in create form if createOnly is false
            if (isCreateForm && restrictions.createOnly === false) {
                return false
            }

            // Hide for non-sense users if senseOnly is true
            if (restrictions.senseOnly && !isSenseUserOrImpersonated) {
                return false
            }

            // Hide for non-sense users if targetSenseOnly is true
            if (restrictions.targetSenseOnly && !isTargetUserFromSense) {
                return false
            }

            return true
        })
        .map(role => ({
            value: role,
            label: getRoleName(role),
            disabled:
                (ROLE_RESTRICTIONS[role]?.senseOnly && !isSenseUserOrImpersonated) ||
                ROLE_RESTRICTIONS[role]?.alwaysRequired, // Disable if always required
        }))
        .sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * Filter roles that current user can see based on restrictions
 * @param {Array} userRoles - Array of roles to filter
 * @param {boolean} isSenseUserOrImpersonated - Whether current user is from Sense or impersonated
 * @param {boolean} isTargetUserFromSense - Whether target user is from Sense
 * @returns {Array} Filtered array of visible roles
 */
export const getVisibleRoles = (userRoles, isSenseUserOrImpersonated, isTargetUserFromSense = false) => {
    return userRoles.filter(role => {
        const restrictions = ROLE_RESTRICTIONS[role]
        if (!restrictions) return true // No restrictions for this role

        // Hide for non-sense users if senseOnly is true
        if (restrictions.senseOnly && !isSenseUserOrImpersonated) {
            return false
        }

        // Hide for non-sense users if targetSenseOnly is true
        if (restrictions.targetSenseOnly && !isTargetUserFromSense) {
            return false
        }

        return true
    })
}

/**
 * Get visible role options for table filters
 * @param {boolean} isSenseUserOrImpersonated - Whether current user is from Sense or impersonated
 * @returns {Array} Array of role filter options with text and value properties
 */
export const getVisibleRoleFilterOptions = isSenseUserOrImpersonated => {
    return Object.values(UserRoles)
        .filter(role => {
            const restrictions = ROLE_RESTRICTIONS[role]
            if (!restrictions) return true

            // Hide for non-sense users if senseOnly is true
            if (restrictions.senseOnly && !isSenseUserOrImpersonated) {
                return false
            }

            return true
        })
        .map(role => ({
            text: getRoleName(role),
            value: role,
        }))
}

/**
 * Merge visible roles with hidden roles for form submission
 * @param {Array} visibleRoles - Roles selected by user in form
 * @param {Array} hiddenRoles - Roles that were hidden from user
 * @returns {Array} Combined array of all roles
 */
export const mergeVisibleAndHiddenRoles = (visibleRoles = [], hiddenRoles = []) => {
    return [...visibleRoles, ...hiddenRoles]
}

/**
 * Separate visible and hidden roles for form editing
 * @param {Array} initialRoles - Initial roles array from user data
 * @param {Array} roleOptions - Available role options for current user
 * @returns {Object} Object with visibleRoles and hiddenRoles arrays
 */
export const separateVisibleAndHiddenRoles = (initialRoles = [], roleOptions = []) => {
    const visibleRoleValues = roleOptions.map(option => option.value)
    const visibleRoles = initialRoles.filter(role => visibleRoleValues.includes(role))
    const hiddenRoles = initialRoles.filter(role => !visibleRoleValues.includes(role))

    // Ensure REGULAR is always in visible roles if it exists in options
    const regularInOptions = roleOptions.some(option => option.value === UserRoles.REGULAR)
    if (regularInOptions && !visibleRoles.includes(UserRoles.REGULAR)) {
        visibleRoles.push(UserRoles.REGULAR)
    }

    return { visibleRoles, hiddenRoles }
}
